import * as React from 'react';
import { Box, Button, Link, Paper } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';

import { PRODUCTS_LOGOS } from '../../helpers/newDashboard';
import { renderHTMLString } from 'helpers/helpers';
import { useParams } from 'react-router-dom';
import { drawerAction } from 'slices/drawer';
import { useAppDispatch } from 'combineReducers';
import { IOnlineTrainingCredits } from 'types/onlineTrainingCredits';
import { useTranslation } from 'react-i18next';
import { LoadingOverlay } from '@pec/aion-ui-components/components/LoadingOverlay';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      minHeight: 350,
      position: 'relative',
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2)
    },
    logo: {
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(2)
    },
    title: {
      color: 'var(--on-surface-high-emphasis-000000-87-)',
      fontSize: 24,
      lineHeight: '24px',
      fontWeight: theme.typography.fontWeightRegular,
      letterSpacing: 0.18,
      marginBottom: theme.spacing(3)
    },
    subtitle: {
      color: 'var(--on-surface-high-emphasis-000000-87-)',
      fontSize: 14,
      lineHeight: '20px',
      fontWeight: theme.typography.fontWeightMedium,
      letterSpacing: 0.1,
      marginBottom: theme.spacing(3)
    },
    description: {
      color: 'var(--on-surface-high-emphasis-000000-87-)',
      fontSize: 14,
      lineHeight: '20px',
      fontWeight: theme.typography.fontWeightRegular,
      letterSpacing: 0.25,
      marginBottom: theme.spacing(3)
    },
    icon: {
      fontSize: 20,
      marginRight: theme.spacing(2)
    },
    productIcon: {
      fontSize: 20,
      marginRight: theme.spacing(1)
    },
    email: {
      marginBottom: theme.spacing(2)
    },
    button: {
      letterSpacing: 1.25,
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      marginRight: theme.spacing(2)
    }
  })
);

type Props = {
  credits: IOnlineTrainingCredits;
  SSOUrl: string;
  handleLogin(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
  isLoading: boolean;
};

export const OnlineTrainingProduct: React.FC<Props> = ({ credits, SSOUrl, isLoading, handleLogin }) => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('subscriber');
  const Logo = PRODUCTS_LOGOS.onlineTraining;
  const training = {
    pending: {
      description: `Your account has been set up and is currently <b>pending review</b>. Please check back in 24 hours.<br/><br/>For further information, please contact Human Focus Support:`,
      contacts: {
        email: 'enquiries@humanfocus.co.uk',
        tel: '01737 657882'
      }
    },
    approved: {
      subtitle: 'Purchased courses:',
      buttons: [
        { label: 'Buy training bundles', url: `/organizations/${organizationId}/online-training` },
        { label: 'Log In', url: SSOUrl }
      ]
    }
  };

  const renderOption = () => {
    if (credits.IsPending) {
      return (
        <>
          <Typography
            style={{ paddingRight: !credits.IsPending ? 0 : 60 }}
            variant="body1"
            classes={{ root: classes.description }}
            dangerouslySetInnerHTML={{
              __html: renderHTMLString(training.pending.description)
            }}
          />
          <Box display="flex" alignItems="end" className={classes.email}>
            <MailOutlineIcon classes={{ root: classes.icon }} color="primary" />
            <Link>{training.pending.contacts.email}</Link>
          </Box>
          <Box display="flex" alignItems="end">
            <PhoneOutlinedIcon classes={{ root: classes.icon }} color="primary" />
            <span>{training.pending.contacts.tel}</span>
          </Box>
        </>
      );
    }
    return (
      <>
        {Object.entries(credits.Breakdown)
          .filter((_, index) => index <= 3)
          .map(([products, workers]) => (
            <Box display="flex" alignItems="end" marginBottom={1} key={products}>
              <CheckCircleOutlineOutlinedIcon classes={{ root: classes.productIcon }} color="primary" />
              <span>
                {products} - {`${workers} worker${workers <= 1 ? '' : 's'}`}
              </span>
            </Box>
          ))}
        {Object.entries(credits.Breakdown).length > 4 && (
          <Box display="flex">
            <Button color="primary" onClick={() => dispatch(drawerAction.openDrawer())}>
              {t('onlineTraining.card.btnSeeMore', 'SEE MORE...')}
            </Button>
          </Box>
        )}
      </>
    );
  };

  return (
    <LoadingOverlay isLoading={isLoading}>
      <Paper classes={{ root: classes.container }}>
        <Box>
          <Typography variant="h6" classes={{ root: classes.title }}>
            {t('onlineTraining.title', 'Online Training')}
          </Typography>
          <Logo className={classes.logo} />
          {!credits.IsPending && (
            <Typography variant="subtitle1" classes={{ root: classes.subtitle }}>
              {training.approved.subtitle}
            </Typography>
          )}
          {renderOption()}
        </Box>
        {!credits.IsPending && (
          <Box display="flex">
            {training.approved.buttons.map(({ label, url }, index) => (
              <Link key={index} href={url} target="_blank">
                <Button
                  color="primary"
                  variant="contained"
                  classes={{ root: classes.button }}
                  onClick={event => handleLogin(event)}
                >
                  {label}
                </Button>
              </Link>
            ))}
          </Box>
        )}
      </Paper>
    </LoadingOverlay>
  );
};
